import React, { useState, useEffect } from 'react';
import {
  BookOpenIcon,
  PencilIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon,
  PaintBrushIcon,
  LanguageIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// Define valid exercise types
export type ArtsHumanitiesExerciseType = 
  | 'literary-analysis' 
  | 'historical-timeline' 
  | 'art-criticism' 
  | 'language-translation' 
  | 'philosophy-argument';

// Helper function to check if exercise type is valid
export const isValidArtsHumanitiesExerciseType = (type: string): type is ArtsHumanitiesExerciseType => {
  const validTypes: ArtsHumanitiesExerciseType[] = [
    'literary-analysis',
    'historical-timeline', 
    'art-criticism',
    'language-translation',
    'philosophy-argument'
  ];
  return validTypes.includes(type as ArtsHumanitiesExerciseType);
};

interface ArtsHumanitiesInteractiveProps {
  content: {
    id: string;
    title: string;
    description: string;
    type: string;
    interactiveData: {
      exerciseType: ArtsHumanitiesExerciseType;
      instructions: string;
      scenario?: any;
      expectedResults?: any;
      hints?: string[];
    };
  };
  onComplete: (completed: boolean, score?: number) => void;
}

const ArtsHumanitiesInteractive: React.FC<ArtsHumanitiesInteractiveProps> = ({
  content,
  onComplete
}) => {
  const [inputs, setInputs] = useState<Record<string, any>>({});
  const [results, setResults] = useState<Record<string, any>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [currentHint, setCurrentHint] = useState(0);
  
  // Tracking states
  const [startTime] = useState(new Date());
  const [timeSpent, setTimeSpent] = useState(0);
  const [windowFocused, setWindowFocused] = useState(true);
  const [tabSwitches, setTabSwitches] = useState(0);
  const [inputInteractions, setInputInteractions] = useState(0);
  const [minimumEngagementTime] = useState(90); // 90 seconds minimum for humanities
  const [isEngaged, setIsEngaged] = useState(false);

  const { exerciseType, instructions, scenario, hints } = content.interactiveData;

  // Debug logging and validation for exercise type
  useEffect(() => {
    console.log('ArtsHumanitiesInteractive Debug:', {
      exerciseType,
      contentType: content.type,
      interactiveData: content.interactiveData,
      isValidType: isValidArtsHumanitiesExerciseType(exerciseType || '')
    });

    if (exerciseType && !isValidArtsHumanitiesExerciseType(exerciseType)) {
      console.error(`Invalid arts/humanities exercise type: "${exerciseType}"`);
      toast.error(`Invalid exercise type: "${exerciseType}". Please check the configuration.`);
    }
  }, [exerciseType, content]);

  useEffect(() => {
    initializeExercise();
  }, [exerciseType]);

  // Window focus tracking
  useEffect(() => {
    if (isSubmitted) return;

    const handleFocus = () => setWindowFocused(true);
    const handleBlur = () => {
      setWindowFocused(false);
      setTabSwitches(prev => prev + 1);
      if (tabSwitches >= 2) {
        toast('⚠️ Stay focused on your analysis for accurate assessment.', {
          icon: '📚',
          duration: 3000,
        });
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setWindowFocused(false);
      } else {
        setWindowFocused(true);
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSubmitted, tabSwitches]);

  // Time tracking
  useEffect(() => {
    if (isSubmitted) return;

    const interval = setInterval(() => {
      if (windowFocused) {
        setTimeSpent(prev => {
          const newTime = prev + 1;
          if (newTime >= minimumEngagementTime && !isEngaged) {
            setIsEngaged(true);
          }
          return newTime;
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [windowFocused, isSubmitted, minimumEngagementTime, isEngaged]);

  const initializeExercise = () => {
    if (!exerciseType) {
      console.warn('ArtsHumanitiesInteractive: No exerciseType provided');
      setInputs({});
      return;
    }

    switch (exerciseType) {
      case 'literary-analysis':
        setInputs({
          theme: '',
          evidence: ['', '', ''],
          interpretation: '',
          conclusion: ''
        });
        break;
      case 'historical-timeline':
        setInputs({
          events: scenario?.events?.map((e: any, i: number) => ({
            ...e,
            userOrder: i,
            correctOrder: e.order
          })) || []
        });
        break;
      case 'art-criticism':
        setInputs({
          visualElements: '',
          composition: '',
          technique: '',
          meaning: '',
          context: ''
        });
        break;
      case 'language-translation':
        setInputs({
          translation: '',
          grammar: '',
          context: ''
        });
        break;
      case 'philosophy-argument':
        setInputs({
          premise1: '',
          premise2: '',
          conclusion: '',
          counterargument: '',
          response: ''
        });
        break;
      default:
        console.warn(`ArtsHumanitiesInteractive: Unknown exercise type "${exerciseType}"`);
        setInputs({});
        break;
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    setInputInteractions(prev => prev + 1);
  };

  const validateSubmission = () => {
    if (!isEngaged) {
      toast.error(`Please spend at least ${Math.ceil(minimumEngagementTime / 60)} minutes engaging with the material.`);
      return false;
    }

    const requiredFields = getRequiredFields();
    const missingFields = requiredFields.filter(field => !inputs[field] || inputs[field].length === 0);
    
    if (missingFields.length > 0) {
      toast.error('Please complete all required fields before submitting.');
      return false;
    }

    return true;
  };

  const getRequiredFields = () => {
    switch (exerciseType) {
      case 'literary-analysis':
        return ['theme', 'interpretation', 'conclusion'];
      case 'historical-timeline':
        return ['events'];
      case 'art-criticism':
        return ['visualElements', 'meaning'];
      case 'language-translation':
        return ['translation'];
      case 'philosophy-argument':
        return ['premise1', 'conclusion'];
      default:
        return [];
    }
  };

  const evaluateSubmission = () => {
    switch (exerciseType) {
      case 'literary-analysis':
        return evaluateLiteraryAnalysis();
      case 'historical-timeline':
        return evaluateTimeline();
      case 'art-criticism':
        return evaluateArtCriticism();
      case 'language-translation':
        return evaluateTranslation();
      case 'philosophy-argument':
        return evaluateArgument();
      default:
        return { score: 0, feedback: 'Unknown exercise type' };
    }
  };

  const evaluateLiteraryAnalysis = () => {
    let score = 0;
    const feedback = [];

    // Check theme identification (30 points)
    if (inputs.theme?.toLowerCase().includes(scenario?.expectedTheme?.toLowerCase())) {
      score += 30;
      feedback.push('✓ Theme correctly identified');
    } else {
      feedback.push('Consider the central message or meaning of the text');
    }

    // Check evidence quality (40 points)
    const validEvidence = inputs.evidence?.filter((e: string) => e.length > 20).length || 0;
    score += Math.min(40, validEvidence * 13);
    if (validEvidence >= 2) {
      feedback.push('✓ Good textual evidence provided');
    } else {
      feedback.push('Provide specific quotes or examples from the text');
    }

    // Check interpretation depth (30 points)
    if (inputs.interpretation?.length > 100) {
      score += 30;
      feedback.push('✓ Thoughtful interpretation provided');
    } else {
      feedback.push('Expand your analysis - explain the significance of your evidence');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateTimeline = () => {
    const events = inputs.events || [];
    let correctPlacements = 0;
    
    events.forEach((event: any, index: number) => {
      if (event.correctOrder === index) {
        correctPlacements++;
      }
    });

    const score = Math.round((correctPlacements / events.length) * 100);
    const feedback = `You placed ${correctPlacements} out of ${events.length} events correctly in chronological order.`;
    
    return { score, feedback };
  };

  const evaluateArtCriticism = () => {
    let score = 0;
    const feedback = [];

    // Visual elements analysis (25 points)
    if (inputs.visualElements?.length > 50) {
      score += 25;
      feedback.push('✓ Visual elements well analyzed');
    }

    // Composition analysis (25 points) 
    if (inputs.composition?.length > 30) {
      score += 25;
      feedback.push('✓ Composition discussed');
    }

    // Technique analysis (25 points)
    if (inputs.technique?.length > 30) {
      score += 25;
      feedback.push('✓ Artistic technique addressed');
    }

    // Meaning interpretation (25 points)
    if (inputs.meaning?.length > 50) {
      score += 25;
      feedback.push('✓ Meaning and significance explored');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateTranslation = () => {
    const expectedWords = scenario?.expectedTranslation?.toLowerCase().split(' ') || [];
    const userWords = inputs.translation?.toLowerCase().split(' ') || [];
    
    let matchingWords = 0;
    expectedWords.forEach((word: string) => {
      if (userWords.includes(word)) {
        matchingWords++;
      }
    });

    const score = Math.round((matchingWords / expectedWords.length) * 100);
    const feedback = `Translation accuracy: ${matchingWords}/${expectedWords.length} key terms correctly translated.`;
    
    return { score, feedback };
  };

  const evaluateArgument = () => {
    let score = 0;
    const feedback = [];

    // Premise clarity (30 points)
    if (inputs.premise1?.length > 20 && inputs.premise2?.length > 20) {
      score += 30;
      feedback.push('✓ Clear premises stated');
    }

    // Logical conclusion (40 points)
    if (inputs.conclusion?.length > 30) {
      score += 40;
      feedback.push('✓ Conclusion follows from premises');
    }

    // Counterargument consideration (30 points)
    if (inputs.counterargument?.length > 20) {
      score += 30;
      feedback.push('✓ Counterargument addressed');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const handleSubmit = () => {
    if (!validateSubmission()) return;

    const evaluation = evaluateSubmission();
    const finalScore = Math.max(0, Math.min(100, evaluation.score));
    const passed = finalScore >= 70;

    setResults({
      score: finalScore,
      feedback: evaluation.feedback,
      passed,
      timeSpent,
      interactions: inputInteractions
    });

    setIsSubmitted(true);
    setIsCorrect(passed);
    
    onComplete(passed, finalScore);

    if (passed) {
      toast.success(`Excellent work! Score: ${finalScore}%`);
    } else {
      toast.error(`Score: ${finalScore}%. Review the feedback and try again.`);
    }
  };

  const getExerciseIcon = () => {
    switch (exerciseType) {
      case 'literary-analysis': return <BookOpenIcon className="h-6 w-6" />;
      case 'historical-timeline': return <ClockIcon className="h-6 w-6" />;
      case 'art-criticism': return <PaintBrushIcon className="h-6 w-6" />;
      case 'language-translation': return <LanguageIcon className="h-6 w-6" />;
      case 'philosophy-argument': return <AcademicCapIcon className="h-6 w-6" />;
      default: return <BookOpenIcon className="h-6 w-6" />;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderExerciseContent = () => {
    switch (exerciseType) {
      case 'literary-analysis':
        return renderLiteraryAnalysis();
      case 'historical-timeline':
        return renderHistoricalTimeline();
      case 'art-criticism':
        return renderArtCriticism();
      case 'language-translation':
        return renderLanguageTranslation();
      case 'philosophy-argument':
        return renderPhilosophyArgument();
      default:
        return (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h4 className="font-semibold text-red-800 mb-2">❌ Unknown Exercise Type</h4>
            <p className="text-red-700 mb-3">
              Exercise type "{exerciseType}" is not recognized. Please check the configuration.
            </p>
            <div className="bg-red-100 p-3 rounded text-sm">
              <strong>Supported exercise types:</strong>
              <ul className="mt-2 space-y-1 list-disc list-inside">
                <li>literary-analysis</li>
                <li>historical-timeline</li>
                <li>art-criticism</li>
                <li>language-translation</li>
                <li>philosophy-argument</li>
              </ul>
              <p className="mt-2">
                <strong>Current value:</strong> "{exerciseType}"
              </p>
            </div>
          </div>
        );
    }
  };

  const renderLiteraryAnalysis = () => (
    <div className="space-y-6">
      {scenario?.text && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Text for Analysis:</h4>
          <div className="text-sm leading-relaxed italic">
            "{scenario.text}"
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Central Theme *
        </label>
        <input
          type="text"
          value={inputs.theme || ''}
          onChange={(e) => handleInputChange('theme', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
          placeholder="What is the main theme or central message?"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Supporting Evidence
        </label>
        {[0, 1, 2].map((index) => (
          <textarea
            key={index}
            value={inputs.evidence?.[index] || ''}
            onChange={(e) => {
              const newEvidence = [...(inputs.evidence || ['', '', ''])];
              newEvidence[index] = e.target.value;
              handleInputChange('evidence', newEvidence);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 mb-2"
            rows={2}
            placeholder={`Evidence ${index + 1}: Quote or example from the text`}
            disabled={isSubmitted}
          />
        ))}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Interpretation & Analysis *
        </label>
        <textarea
          value={inputs.interpretation || ''}
          onChange={(e) => handleInputChange('interpretation', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
          rows={4}
          placeholder="Explain the significance of your evidence. How does it support your theme?"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Conclusion *
        </label>
        <textarea
          value={inputs.conclusion || ''}
          onChange={(e) => handleInputChange('conclusion', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
          rows={3}
          placeholder="Summarize your analysis and its broader implications"
          disabled={isSubmitted}
        />
      </div>
    </div>
  );

  const renderHistoricalTimeline = () => (
    <div className="space-y-4">
      <p className="text-gray-600">
        Arrange these historical events in chronological order by dragging them:
      </p>
      
      <div className="space-y-2">
        {inputs.events?.map((event: any, index: number) => (
          <div
            key={index}
            className="bg-gray-50 p-3 rounded-lg border-l-4 border-indigo-500 cursor-move"
            draggable={!isSubmitted}
          >
            <div className="font-medium">{event.title}</div>
            <div className="text-sm text-gray-600">{event.description}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderArtCriticism = () => (
    <div className="space-y-6">
      {scenario?.imageUrl && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <img 
            src={scenario.imageUrl} 
            alt="Artwork for analysis"
            className="max-w-full h-auto rounded-lg mx-auto"
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Visual Elements *
        </label>
        <textarea
          value={inputs.visualElements || ''}
          onChange={(e) => handleInputChange('visualElements', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
          rows={3}
          placeholder="Describe the colors, shapes, lines, textures, and forms you observe"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Composition & Technique
        </label>
        <textarea
          value={inputs.composition || ''}
          onChange={(e) => handleInputChange('composition', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
          rows={3}
          placeholder="How are elements arranged? What techniques did the artist use?"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Meaning & Interpretation *
        </label>
        <textarea
          value={inputs.meaning || ''}
          onChange={(e) => handleInputChange('meaning', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
          rows={4}
          placeholder="What message or emotion does this artwork convey? What is its significance?"
          disabled={isSubmitted}
        />
      </div>
    </div>
  );

  const renderLanguageTranslation = () => (
    <div className="space-y-6">
      {scenario?.originalText && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Text to Translate:</h4>
          <div className="text-lg font-medium">
            {scenario.originalText}
          </div>
          <div className="text-sm text-gray-600 mt-2">
            From: {scenario.sourceLanguage} → To: {scenario.targetLanguage}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Translation *
        </label>
        <textarea
          value={inputs.translation || ''}
          onChange={(e) => handleInputChange('translation', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
          rows={3}
          placeholder="Provide your translation here"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Grammar Notes
        </label>
        <textarea
          value={inputs.grammar || ''}
          onChange={(e) => handleInputChange('grammar', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
          rows={2}
          placeholder="Explain any grammatical structures or challenges"
          disabled={isSubmitted}
        />
      </div>
    </div>
  );

  const renderPhilosophyArgument = () => (
    <div className="space-y-6">
      {scenario?.prompt && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Philosophical Question:</h4>
          <div className="text-gray-700">{scenario.prompt}</div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Premise 1 *
        </label>
        <textarea
          value={inputs.premise1 || ''}
          onChange={(e) => handleInputChange('premise1', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
          rows={2}
          placeholder="State your first premise clearly"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Premise 2
        </label>
        <textarea
          value={inputs.premise2 || ''}
          onChange={(e) => handleInputChange('premise2', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
          rows={2}
          placeholder="State your second premise (if applicable)"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Conclusion *
        </label>
        <textarea
          value={inputs.conclusion || ''}
          onChange={(e) => handleInputChange('conclusion', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
          rows={3}
          placeholder="What conclusion follows from your premises?"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Counterargument
        </label>
        <textarea
          value={inputs.counterargument || ''}
          onChange={(e) => handleInputChange('counterargument', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
          rows={2}
          placeholder="What might someone argue against your position?"
          disabled={isSubmitted}
        />
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {getExerciseIcon()}
            <div className="ml-3">
              <h3 className="text-xl font-bold">{content.title}</h3>
              <p className="text-purple-100 text-sm">{content.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {/* Focus indicator */}
            <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
              windowFocused ? 'bg-green-500 bg-opacity-20 text-green-100' : 'bg-red-500 bg-opacity-20 text-red-100'
            }`}>
              <div className={`w-2 h-2 rounded-full mr-1 ${
                windowFocused ? 'bg-green-300' : 'bg-red-300'
              }`}></div>
              {windowFocused ? 'Focused' : 'Not Focused'}
            </div>
            
            <div className="text-sm">
              Time: {formatTime(timeSpent)}
            </div>
            
            {isEngaged && (
              <div className="flex items-center text-xs px-2 py-1 rounded-full bg-green-500 bg-opacity-20 text-green-100">
                <CheckCircleIcon className="w-3 h-3 mr-1" />
                Engaged
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="p-6 bg-gray-50 border-b border-gray-200">
        <p className="text-gray-700">{instructions}</p>
        
        {/* Engagement progress */}
        {!isEngaged && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
              <span>Minimum engagement time</span>
              <span>{formatTime(timeSpent)} / {formatTime(minimumEngagementTime)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min((timeSpent / minimumEngagementTime) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Exercise Content */}
      <div className="p-6">
        {renderExerciseContent()}

        {/* Hints Section */}
        {hints && hints.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => setShowHints(!showHints)}
              className="flex items-center text-indigo-600 hover:text-indigo-700 mb-3"
            >
              <LightBulbIcon className="h-4 w-4 mr-2" />
              {showHints ? 'Hide Hints' : 'Show Hints'}
            </button>
            
            {showHints && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-yellow-800">
                    Hint {currentHint + 1} of {hints.length}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentHint(Math.max(0, currentHint - 1))}
                      disabled={currentHint === 0}
                      className="text-xs px-2 py-1 bg-yellow-200 text-yellow-800 rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentHint(Math.min(hints.length - 1, currentHint + 1))}
                      disabled={currentHint === hints.length - 1}
                      className="text-xs px-2 py-1 bg-yellow-200 text-yellow-800 rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
                <p className="text-yellow-700 text-sm">{hints[currentHint]}</p>
              </div>
            )}
          </div>
        )}

        {/* Submit Button */}
        <div className="mt-6">
          {!isSubmitted ? (
            <button
              onClick={handleSubmit}
              className="bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors"
            >
              Submit Analysis
            </button>
          ) : (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                {isCorrect ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
                ) : (
                  <XCircleIcon className="h-6 w-6 text-red-600 mr-2" />
                )}
                <span className="font-medium">
                  Score: {results.score}% {isCorrect ? '(Passed)' : '(Review Required)'}
                </span>
              </div>
              <div className="text-sm text-gray-600 whitespace-pre-line">
                {results.feedback}
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Time spent: {formatTime(results.timeSpent)} | Interactions: {results.interactions}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArtsHumanitiesInteractive;